import Page from '../../components/Page'
import { Alert, Button, Result, Tag, Typography, message } from 'antd'
import { useTranslation } from 'react-i18next'
import Table from '../../components/Table'
import { useState } from 'react'
import { apiFetch } from '../../api'
import { articleCategoryLabel, articleCategoryValueEnum, compareArticlesForCatalog } from '../../utils/articleCategory'

const formatter = new Intl.NumberFormat('de-DE', {
  style: 'currency',
  currency: 'EUR',
})

// defensive price parsing (HOR-4): parseFloat('') === NaN, and JSON.stringify(NaN) === 'null',
// which the backend rejects with 400 – never let NaN/null through as price
const toPrice = value => {
  const price = typeof value === 'number' ? value : parseFloat(value)

  return Number.isFinite(price) ? price : null
}

// HOR-50: Reihenfolge im Katalog. Ein leeres Feld meint den Vertrags-Default 0; alles
// andere muss eine nicht-negative Ganzzahl sein, sonst null - dann lehnt der Aufrufer ab,
// statt z.B. 1.5 oder -3 zu schicken (das Backend antwortet darauf mit 400 invalidSortOrder).
const toSortOrder = value => {
  if (value === undefined || value === null || value === '') return 0

  const order = typeof value === 'number' ? value : Number(value)

  return Number.isInteger(order) && order >= 0 ? order : null
}

// HOR-50: Anzeige-Reihenfolge wie im Anfrage-Wizard der Kunden-App (Kategorie, Reihenfolge,
// Name), damit der Admin die Wirkung seiner Eingaben direkt sieht. Noch nicht gespeicherte
// Zeilen (id 'null_...') bleiben unten, wo EditableProTable sie anlegt - sonst spraenge die
// Eingabezeile beim Anlegen durch die Tabelle.
const isDraftRow = row => String(row?.id).startsWith('null_')
const sortLikeWizard = rows => [...rows.filter(row => !isDraftRow(row))].sort(compareArticlesForCatalog).concat(rows.filter(isDraftRow))

export default () => {
  useTranslation()
  const [messageApi, contextHolder] = message.useMessage()

  const [pageError, setPageError] = useState(null)
  const [reloadKey, setReloadKey] = useState(0)

  /**
   * Shared result handler for create/edit/delete (HOR-4).
   * Shows a specific translated message and always returns true so the table wrapper's
   * generic "request failed" message is suppressed (HOR-3 pattern, see Services).
   * The wrapper refetches the list after every mutation regardless of the return value,
   * so a stale row (404) disappears automatically and no extra refresh call is needed.
   */
  const handleMutationResult = (result, successKey) => {
    if (result.errorType === 'unauthorized') return true // apiFetch already redirected to login

    if (result.ok) {
      messageApi.open({
        type: 'success',
        content: i18n(successKey),
      })

      return true
    }

    if (result.status === 400) {
      // class-validator answers with { message: [...] } – show one translated summary
      // instead of the raw English backend strings.
      // HOR-50: die Katalogfelder tragen maschinenlesbare Fehlercodes (invalidSortOrder,
      // invalidArticleCategory) - die bekommen eine konkrete Meldung, der Rest die Summary.
      const codes = Array.isArray(result.data?.message) ? result.data.message : [result.data?.message]

      messageApi.open({
        type: 'error',
        content: i18n(
          codes.includes('invalidSortOrder')
            ? 'articles.sortOrderInvalid'
            : codes.includes('invalidArticleCategory')
              ? 'articles.categoryInvalid'
              : 'articles.validationFailed',
        ),
      })
    } else if (result.errorType === 'forbidden') {
      messageApi.open({
        type: 'error',
        content: i18n('errors.noPermission'),
      })
    } else if (result.status === 404) {
      // article was deleted in the meantime – the wrapper's refetch removes the stale row
      messageApi.open({
        type: 'error',
        content: i18n('errors.notFound'),
      })
    } else if (result.errorType === 'network') {
      messageApi.open({
        type: 'error',
        content: i18n('errors.network'),
      })
    } else {
      messageApi.open({
        type: 'error',
        content: i18n('common.requestFailed'),
      })
    }

    return true
  }

  // invalid price should be caught by the column rules – this is the defensive backstop
  // so a NaN price is never sent to the backend
  const rejectInvalidPrice = () => {
    messageApi.open({
      type: 'error',
      content: i18n('articles.priceInvalid'),
    })

    return true
  }

  // HOR-50: gleiches Backstop-Muster fuer die Reihenfolge (InputNumber mit precision 0 und
  // min 0 verhindert das im Normalfall bereits)
  const rejectInvalidSortOrder = () => {
    messageApi.open({
      type: 'error',
      content: i18n('articles.sortOrderInvalid'),
    })

    return true
  }

  /**
   * HOR-50 / HOR-83: Request-Body fuer POST und PATCH /articles.
   * Die vier Alt-Felder (generalService/dialService/restaurationService/optionalService)
   * werden bewusst NICHT mehr geschickt: beim Anlegen bleiben sie null, beim Bearbeiten
   * laesst das Backend fehlende Felder unangetastet (Bestandsdaten fuer Alt-Clients).
   * Rueckgabe { body } oder - bei ungueltigem Preis/ungueltiger Reihenfolge - { error }, wobei
   * error() die Meldung zeigt und wie handleMutationResult true liefert (Wrapper-Toast unterdrueckt).
   */
  const toArticleBody = article => {
    const price = toPrice(article.price)

    if (price === null) return { error: rejectInvalidPrice }

    const sortOrder = toSortOrder(article.sortOrder)

    if (sortOrder === null) return { error: rejectInvalidSortOrder }

    return {
      body: {
        name: article.name,
        // HOR-56: leeres Feld als null schicken, nicht als '' - sonst steht
        // ein leerer englischer Name in der DB und verdraengt den Fallback.
        nameEn: article.nameEn?.trim() || null,
        price,
        // HOR-50: geleertes Select liefert undefined/'' - beides meint "keine Kategorie"
        category: article.category || null,
        // Switch einer neuen Zeile ist undefined -> nicht waehlbar (Vertrags-Default false)
        customerSelectable: !!article.customerSelectable,
        sortOrder,
      },
    }
  }

  const columns = [
    {
      title: i18n('articles.columns.name'),
      key: 'name',
      dataIndex: 'name',
      formItemProps: {
        rules: [
          {
            required: true,
            message: i18n('articles.nameRequired'),
          },
        ],
      },
    },
    {
      // HOR-56: Artikelnamen erschienen in der Kunden-App unabhaengig von der
      // Sprache auf Deutsch. `name` bleibt Pflicht und Fallback, `nameEn` ist
      // optional - ungepflegte Artikel verhalten sich unveraendert.
      title: i18n('articles.columns.nameEn'),
      key: 'nameEn',
      dataIndex: 'nameEn',
      tooltip: i18n('articles.nameEnHint'),
    },
    {
      title: i18n('articles.columns.price'),
      key: 'price',
      dataIndex: 'price',
      valueType: 'digit',
      width: 120,
      formItemProps: {
        rules: [
          {
            required: true,
            message: i18n('articles.priceRequired'),
          },
          {
            type: 'number',
            message: i18n('articles.priceInvalid'),
          },
        ],
      },
      render: (_, { price }) => {
        return <div>{formatter.format(price)}</div>
      },
    },
    {
      // HOR-50 / HOR-83: ersetzt die vier Alt-Spalten "Zifferblatt/Rest./Allg./Zusatz
      // Serviceart". Die Kategorie bestimmt, unter welcher Ueberschrift der Artikel im
      // Anfrage-Wizard der Kunden-App erscheint; leer = keine Zuordnung.
      title: i18n('articles.columns.category'),
      tooltip: i18n('articles.categoryHint'),
      key: 'category',
      dataIndex: 'category',
      valueType: 'select',
      width: 230,
      valueEnum: articleCategoryValueEnum(),
      fieldProps: {
        allowClear: true,
        placeholder: i18n('articles.categoryNone'),
      },
      // ProTable uebergibt das gerenderte valueEnum-DOM als erstes Argument - Rohwert aus
      // dem Record lesen (Muster aus Users/Services)
      render: (_, { category }) =>
        category ? <Tag>{articleCategoryLabel(category)}</Tag> : <Typography.Text type="secondary">{i18n('articles.categoryNone')}</Typography.Text>,
    },
    {
      // HOR-50: nur waehlbare Artikel landen im Kunden-Katalog (GET /articles/catalog)
      title: i18n('articles.columns.customerSelectable'),
      tooltip: i18n('articles.customerSelectableHint'),
      key: 'customerSelectable',
      dataIndex: 'customerSelectable',
      valueType: 'switch',
      width: 170,
      render: (_, { customerSelectable }) => <Tag color={customerSelectable ? 'green' : 'default'}>{customerSelectable ? i18n('common.yes') : i18n('common.no')}</Tag>,
    },
    {
      // HOR-50: Reihenfolge innerhalb der Kategorie, kleinere Zahl zuerst
      title: i18n('articles.columns.sortOrder'),
      tooltip: i18n('articles.sortOrderHint'),
      key: 'sortOrder',
      dataIndex: 'sortOrder',
      valueType: 'digit',
      width: 120,
      fieldProps: {
        min: 0,
        precision: 0,
      },
      formItemProps: {
        rules: [
          {
            type: 'integer',
            min: 0,
            message: i18n('articles.sortOrderInvalid'),
          },
        ],
      },
      // Rohwert aus dem Record; ein fehlender Wert (Alt-Backend) zeigt den DB-Default 0
      render: (_, { sortOrder }) => toSortOrder(sortOrder) ?? 0,
    },
    {
      title: i18n('common.createdAt'),
      key: 'createdAt',
      dataIndex: 'createdAt',
      readonly: true,
      width: 140,
      // HOR-90: Rohwert aus dem Record lesen (siehe Users/Services)
      render: (_, record) => moment(record.createdAt).fromNow(),
    },
  ]

  return (
    <Page title={i18n('articles.title')}>
      {contextHolder}
      {pageError && (
        <Result
          status={pageError === 'forbidden' ? '403' : pageError === 'network' ? 'warning' : 'error'}
          title={pageError === 'forbidden' ? i18n('errors.noPermission') : pageError === 'network' ? i18n('errors.network') : i18n('errors.loadFailed')}
          extra={
            // retrying a 403 will not help – only offer retry for transient errors (HOR-1 pattern)
            pageError !== 'forbidden' && (
              <Button
                type="primary"
                onClick={() => {
                  setPageError(null)
                  setReloadKey(key => key + 1)
                }}
              >
                {i18n('common.retry')}
              </Button>
            )
          }
        />
      )}
      {!pageError && (
        <>
          {/* HOR-50 / HOR-83: kurze Erklaerung, wie die Katalogfelder die Kunden-App steuern */}
          <Alert type="info" showIcon style={{ marginBottom: 16 }} message={i18n('articles.catalogHint.title')} description={i18n('articles.catalogHint.text')} />
          <Table
            key={reloadKey}
            columns={columns}
            filter={sortLikeWizard}
            modes={['create', 'edit', 'delete']}
            title={i18n('articles.tableTitle')}
            onDelete={async article => {
              const result = await apiFetch('/articles', {
                method: 'DELETE',
                body: {
                  id: article.id,
                },
              })

              return handleMutationResult(result, 'articles.deleted')
            }}
            onCreate={async article => {
              const { body, error } = toArticleBody(article)

              if (error) return error()

              const result = await apiFetch('/articles', {
                method: 'POST',
                body,
              })

              return handleMutationResult(result, 'articles.saved')
            }}
            onEdit={async article => {
              const { body, error } = toArticleBody(article)

              if (error) return error()

              const result = await apiFetch('/articles', {
                method: 'PATCH',
                body: {
                  id: article.id,
                  ...body,
                },
              })

              return handleMutationResult(result, 'articles.saved')
            }}
            request={async () => {
              const result = await apiFetch('/articles')

              if (!result.ok) {
                setPageError(result.errorType)

                return {
                  data: [],
                  total: 0,
                  success: false,
                }
              }

              const articles = Array.isArray(result.data) ? result.data : []

              return {
                data: articles,
                total: articles.length,
                success: true,
              }
            }}
          />
        </>
      )}
    </Page>
  )
}

// HOR-50: Die frueheren Exporte SERVICE_KEYS/RESTAURATION_KEYS/DIAL_KEYS/OPTIONAL_KEYS
// (HOR-79) sind mit den vier Alt-Spalten entfallen; die Labels der Alt-Enums liegen
// weiter unter `serviceOptions.*` und werden nur noch in der Vorgangsansicht fuer
// Anfragen aus dem alten Formular benoetigt.
